//@ts-nocheck
import { Fragment, useEffect, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import Modal from "../common/Modal";
import { createTeamsPayload } from "../../features/skills/skillsTypes";
import { toast } from "sonner";
import {
  createLocation,
  fetchPipelineStagesList,
  updateLocation,
} from "../../features/skills/skillsSlice";
import {
  CheckCircleColorIcon,
  ChevronDownIconWhite,
  CustomCheckIcon,
  CustomPlusColorIcon,
  EllipsisHorizontalCircleColorIcon,
  XCircleColorIcon,
} from "../icons";
import { useAppDispatch } from "../../app/hooks";

interface BoardPageProps {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  editBoardData?: any;
  baseId: number;
  setEditBoardData: (value: any) => void;
  userId: number;
}

const people = [
  { id: 1, name: "inbox", icon: CustomPlusColorIcon, color: "text-[#4b5563]" },
  {
    id: 2,
    name: "screening",
    icon: EllipsisHorizontalCircleColorIcon,
    color: "text-[#4338ca]",
  },
  { id: 3, name: "hired", icon: CheckCircleColorIcon, color: "text-[#15803d]" },
  { id: 4, name: "reject", icon: XCircleColorIcon, color: "text-[#b91c1c]" },
];

const competenceLevels = [
  {
    level: 1,
    title: "Basic (0-1 years)",
    title1: "Basic",
    description: [
      "Fundamental understanding of the skill.",
      "Limited hands-on experience; requires training and supervision.",
      "Can assist with simple tasks but not independently.",
    ],
  },
  {
    level: 2,
    title: "Intermediate (1-3 years)",
    title1: "Intermediate",
    description: [
      "Practical knowledge and some hands-on experience.",
      "Can perform tasks with occasional guidance.",
      "Understands core concepts but may struggle with complex problems.",
    ],
  },
  {
    level: 3,
    title: "Proficient (3-5 years)",
    title1: "Proficient",
    description: [
      "Solid experience and ability to work independently.",
      "Can solve most challenges within the domain.",
      "Requires minimal supervision and can mentor junior team members.",
    ],
  },
  {
    level: 4,
    title: "Advanced (5-8 years)",
    title1: "Advanced",
    description: [
      "Deep expertise and strong problem-solving ability.",
      "Can handle complex challenges and optimize processes.",
      "Often mentors others and contributes to strategic decisions.",
    ],
  },
  {
    level: 5,
    title: "Expert (8+ years)",
    title1: "Expert",
    description: [
      "Industry leader or authority in the skill.",
      "Develops best practices, frameworks, and innovations.",
      "Drives strategic initiatives and coaches teams at all levels.",
    ],
  },
];

// Auto-resizing textarea component
const AutoResizeTextarea = ({ value, onChange, placeholder }: any) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.max(60, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      className="resize-none outline-none bg-transparent focus:border-light-gray-200 focus:outline-none focus:!shadow-none focus:ring-transparent !ring-0 text-[14px]
       text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0] 
      border
       border-solid border-gray-300 rounded-md p-2"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={1}
    />
  );
};

const CreateScoreCardSkill: React.FC<BoardPageProps> = ({
  isModalOpen,
  setIsModalOpen,
  editBoardData,
  baseId,
  setEditBoardData,
  userId,
}) => {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [color, setColor] = useState("bg-[#3b82f6]");
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [purposeError, setPurposeError] = useState("");
  const dispatch = useAppDispatch();
  const [baseTeamsData, setBaseTeamsData] = useState<any[]>([]);
  const [selected, setSelected] = useState(people[0]);
  const [value, setValue] = useState(3);
  const [message, setMessage] = useState("");
  const [showCompetenceDetails, setShowCompetenceDetails] = useState(false);

  const handleChange = (e: any) => {
    setValue(parseInt(e.target.value));
    setShowCompetenceDetails(true);
  };

  const colorList = [
    { name: "bg-[#3b82f6]" },
    { name: "bg-[#6b7280]" },
    { name: "bg-[#bfdbfe]" },
    { name: "bg-[#ec4899]" },
    { name: "bg-[#6366f1]" },
    { name: "bg-[#eab308]" },
    { name: "bg-[#22c55e]" },
    { name: "bg-[#06b6d4]" },
    { name: "bg-[#dc2626]" },
  ];

  // Load edit data when available
  useEffect(() => {
    if (editBoardData) {
      setName(editBoardData.skill_name || "");
      setColor(`bg-[${editBoardData.skill_colour}]` || "bg-[#3b82f6]");
      // setChecked(prevChecked => {
      //     return !!editBoardData.comment_required;
      // });
      setMessage(editBoardData.detail || "");
      setValue(editBoardData.skill_scale);
      const matchingBucket = people.find(
        (person) => person.name === editBoardData.bucket,
      );
      if (matchingBucket) {
        setSelected(matchingBucket);
      } else {
        setSelected(people[0]);
      }
    }
  }, [editBoardData]);

  useEffect(() => {
    const storedData = localStorage.getItem("baseSkillsData");
    if (storedData) {
      setBaseTeamsData(JSON.parse(storedData));
    }
  }, []);

  const handleSave = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setNameError("");
    setPurposeError("");

    if (name.trim().length === 0) {
      setNameError("Name is required");
      setLoading(false);
      return;
    }
    // Check if bucket is selected
    if (!selected || !selected.name) {
      setPurposeError("Bucket is required");
      setLoading(false);
      return;
    }

    const colorValue = color.substring(
      color.indexOf("[#") + 1,
      color.indexOf("]"),
    );

    const payload: createTeamsPayload = {
      base_id: baseId,
      skill_name: name,
      skill_colour: colorValue,
      skill_scale: value,
      scorecard_id: userId,
      // comment_required: privacy,
      detail: message,
      // bucket: selected.name
    };

    try {
      if (editBoardData) {
        // Update existing team
        const response = await dispatch(
          updateLocation({
            payload,
            baseId: baseId,
            id: editBoardData.id,
          }),
        );

        if (response.meta.requestStatus === "fulfilled") {
          toast.success("Skills updated successfully!");
        } else {
          toast.error("Failed to update team.");
        }
      } else {
        // Create new team
        const response = await dispatch(
          createLocation({ payload, userId: baseId }),
        );
        if (response.meta.requestStatus === "fulfilled") {
          toast.success("Create Skills successfully!");
        } else if (response.meta.requestStatus === "rejected") {
          toast.error("Skills exists in base");
        } else {
          toast.error("Failed to create team.");
        }
      }

      setIsModalOpen(false);
      resetForm();

      // Fetch updated list
      const baseResponse = await dispatch(
        fetchPipelineStagesList({ baseId: Number(baseId), userId }),
      );
      if (baseResponse?.payload) {
        localStorage.setItem(
          "baseSkillsData",
          JSON.stringify(baseResponse.payload),
        );
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "baseSkillsData",
            newValue: JSON.stringify(baseResponse.payload),
          }),
        );
      }
    } catch (error) {
      console.error("Failed to save team:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setPurpose("");
    setColor("bg-[#3b82f6]");
    // setChecked(false);
    setNameError("");
    setMessage("");
    setValue(1);
    setEditBoardData(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "name") {
      setName(value);
      setNameError(value.trim().length === 0 ? "Name is required" : "");
    } else if (name === "purpose") {
      setPurpose(value);
      // setPurposeError(value.trim().length === 0 ? "Description is required" : "");
    }
  };

  // Get current competence level details
  const currentCompetenceLevel = competenceLevels.find(
    (level) => level.level === value,
  );

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        resetForm();
      }}
      title={editBoardData ? "Edit Skills" : "Create Skills"}
      onSave={handleSave}
      loading={loading}
    >
      <form action="#" onSubmit={handleSave} className="space-y-6 mt-5">
        <div className="flex flex-col gap-y-2">
          <label className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6">
            Name
          </label>
          <div className="relative">
            <Menu as="div" className="flex items-center">
              <Menu.Button className="absolute inset-y-0 left-0 flex items-center px-2">
                <div
                  className={`h-6 w-6 rounded-full ${color} border flex justify-center items-center`}
                >
                  <ChevronDownIconWhite />
                </div>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute flex flex-wrap left-0 top-8 z-10 mt-2.5 w-44 p-2 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                  {colorList.map((value, index) => (
                    <div
                      onClick={() => setColor(value.name)}
                      key={index}
                      className={`flex h-6 w-6 border ${value.name} rounded-full m-2 justify-center items-center cursor-pointer`}
                    >
                      {color === value.name && <CustomCheckIcon />}
                    </div>
                  ))}
                </Menu.Items>
              </Transition>
              <input
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={handleInputChange}
                placeholder="Enter Name"
                className="flex w-full grow border pl-10 pr-4 py-2 text-sm leading-6 shadow-[0 1px 2px 0 rgba(18, 18, 23, 0.05)] transition-colors duration-100 placeholder:text-surface-500 outline-indigo-600
                    focus:outline focus:outline-2 focus:-outline-offset-1 bg-background text-text-100 hover:border-[#a9a9bc] dark:hover:border-surface-200
                    border-[#d1d1db] dark:border-surface-100 focus:outline-indigo-600 h-[36px] rounded-[0.375rem]"
              />
            </Menu>
          </div>
          {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          <div className="pt-4 mb-[-5px]">
            <h2 className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6 mb-2">
              Skill Details
            </h2>
            <AutoResizeTextarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a details..."
            />
          </div>

          <div className="flex flex-col gap-y-0 mt-4">
            <label className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6">
              Skill Competence Requirements Scale
            </label>
            <div className="w-full max-w-full mx-auto rounded-md border border-gray-300 p-2 mt-2">
              <input
                type="range"
                min="1"
                max="5"
                value={value}
                onChange={handleChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />

              <div className="flex justify-between px-2 mt-2 cursor-pointer">
                {competenceLevels.map((level) => (
                  <span
                    key={level.level}
                    onClick={() => {
                      setValue(level.level);
                      setShowCompetenceDetails(true);
                    }}
                    // className={`text-sm font-bold ${level.level <= level ? 'text-blue-500' : 'text-gray-400'}`}
                    className={`text-sm font-bold ${level.level === value ? "text-blue-500" : "text-gray-400"}`}
                  >
                    {level.title1}
                  </span>
                ))}
              </div>

              {showCompetenceDetails && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <h3 className="font-medium text-blue-600">
                    {currentCompetenceLevel?.title}
                  </h3>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {currentCompetenceLevel?.description.map((item, index) => (
                      <li key={index} className="text-sm text-gray-700">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CreateScoreCardSkill;
